import { animeList } from "@/mocks/anime";
import { newsList } from "@/mocks/news";
import { characterList } from "@/mocks/characters";
import { specialList } from "@/mocks/specials";
import type { Anime, NewsItem, Character, Special } from "@/types";

export type ContentType = "anime" | "news" | "character";

export interface ResolvedContent {
  type: ContentType | null;
  anime?: Anime;
  news?: NewsItem;
  character?: Character;
}

export function resolveContent(id: string): ResolvedContent {
  if (id.startsWith("anime-")) {
    const found = animeList.find((item) => item.id === id);
    if (found) return { type: "anime", anime: found };
  }
  if (id.startsWith("news-")) {
    const found = newsList.find((item) => item.id === id);
    if (found) return { type: "news", news: found };
  }
  if (id.startsWith("char-")) {
    const found = characterList.find((item) => item.id === id);
    if (found) return { type: "character", character: found };
  }
  return { type: null };
}

export function getSpecialById(id: string): Special | undefined {
  return specialList.find((item) => item.id === id);
}

export function getAnimeById(id: string): Anime | undefined {
  return animeList.find((item) => item.id === id);
}

export function getNewsById(id: string): NewsItem | undefined {
  return newsList.find((item) => item.id === id);
}

export function getCharacterById(id: string): Character | undefined {
  return characterList.find((item) => item.id === id);
}

export function sortByMode<T extends { views: number; publishTime: string; rating?: number }>(
  list: T[],
  mode: string,
): T[] {
  const copy = [...list];
  if (mode === "热门") {
    return copy.sort((a, b) => b.views - a.views);
  }
  if (mode === "评分") {
    return copy.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
  }
  return copy.sort(
    (a, b) => new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime(),
  );
}

export interface SearchResult {
  id: string;
  type: ContentType;
  title: string;
  description: string;
  image: string;
  meta: string;
  tags: string[];
}

export function searchContent(keyword: string, type?: ContentType | "all"): SearchResult[] {
  const kw = keyword.trim().toLowerCase();
  const results: SearchResult[] = [];

  const match = (text: string) => !kw || text.toLowerCase().includes(kw);

  if (!type || type === "all" || type === "anime") {
    animeList.forEach((item) => {
      const haystack = `${item.title} ${item.description} ${item.genre.join(" ")} ${item.tags.join(" ")} ${item.region} ${item.year}`;
      if (match(haystack)) {
        results.push({
          id: item.id,
          type: "anime",
          title: item.title,
          description: item.description,
          image: item.cover,
          meta: `${item.year} · ${item.region} · 评分 ${item.rating}`,
          tags: item.genre,
        });
      }
    });
  }

  if (!type || type === "all" || type === "news") {
    newsList.forEach((item) => {
      const haystack = `${item.title} ${item.summary} ${item.category} ${item.author}`;
      if (match(haystack)) {
        results.push({
          id: item.id,
          type: "news",
          title: item.title,
          description: item.summary,
          image: item.cover,
          meta: `${item.publishTime} · ${item.category}`,
          tags: [item.category],
        });
      }
    });
  }

  if (!type || type === "all" || type === "character") {
    characterList.forEach((item) => {
      const haystack = `${item.name} ${item.animeTitle} ${item.description} ${item.roleType} ${item.tags.join(" ")}`;
      if (match(haystack)) {
        results.push({
          id: item.id,
          type: "character",
          title: item.name,
          description: item.description,
          image: item.avatar,
          meta: `《${item.animeTitle}》 · ${item.roleType}`,
          tags: item.tags,
        });
      }
    });
  }

  return results;
}